import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { AlertTriangle, Package } from "lucide-react";
import { Button } from "@/components/ui/button";
import { usePermissoes } from "@/contexts/PermissoesContext";
import { FORMULARIOS } from "@/domain/formularios";
import { estoqueService } from "@/features/estoque/services/estoqueService";

const MAX_ITENS = 5;

const fmtQtd = (v: number) =>
  v.toLocaleString("pt-BR", { maximumFractionDigits: 2 });

export function StockAlerts({ modoEdicao = false }: { modoEdicao?: boolean } = {}) {
  const navigate = useNavigate();
  const { temPermissao } = usePermissoes();
  const podeVer = temPermissao(FORMULARIOS.ESTOQUE_REPOSICAO, "ler");

  const { data = [], isLoading } = useQuery({
    queryKey: ["estoque-reposicao"],
    queryFn: estoqueService.getReposicao,
    enabled: podeVer,
    staleTime: 60_000,
  });

  // Sem permissão, ou empresa sem produtos abaixo do mínimo (ex.: prestação de
  // serviço sem controle de estoque) → o card não aparece. No modo edição do
  // dashboard o shell é mantido para o widget poder ser movido/removido.
  const vazio = !podeVer || (!isLoading && data.length === 0);
  if (vazio && !modoEdicao) return null;

  const itens = [...data]
    .sort((a, b) => {
      const pctA = a.estoqueMinimo > 0 ? a.saldoAtual / a.estoqueMinimo : 0;
      const pctB = b.estoqueMinimo > 0 ? b.saldoAtual / b.estoqueMinimo : 0;
      return pctA - pctB;
    })
    .slice(0, MAX_ITENS);

  return (
    <Card className="shadow-card border-border/50 h-full flex flex-col">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 font-heading text-base">
          <div className="flex h-7 w-7 items-center justify-center rounded-md bg-warning/10">
            <AlertTriangle className="h-4 w-4 text-warning" />
          </div>
          Alertas de Estoque
        </CardTitle>
        <CardDescription className="text-xs">Produtos abaixo do estoque mínimo</CardDescription>
      </CardHeader>
      <CardContent className="flex-1 overflow-auto">
        {vazio ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            Nenhum produto abaixo do estoque mínimo.
          </p>
        ) : isLoading ? (
          <div className="space-y-2.5">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-14 w-full rounded-lg" />
            ))}
          </div>
        ) : (
          <div className="space-y-2.5">
            {itens.map((item) => {
              const critico = item.saldoAtual <= 0;
              return (
                <div key={`${item.subProdutoId}-${item.localEstoqueId}`} className="flex items-center justify-between p-3 rounded-lg bg-muted/40 border border-border/30">
                  <div className="flex items-center gap-3">
                    <Package className={`h-4 w-4 ${critico ? "text-destructive" : "text-warning"}`} />
                    <div>
                      <p className="text-sm font-medium text-foreground">{item.descricao}</p>
                      <p className="text-xs text-muted-foreground">
                        Estoque: <span className={critico ? "text-destructive font-medium" : "text-warning font-medium"}>{fmtQtd(item.saldoAtual)}</span> · Mínimo: {fmtQtd(item.estoqueMinimo)} · {item.localDescricao}
                      </p>
                    </div>
                  </div>
                  <Button size="sm" variant="outline" className="shrink-0 text-xs h-8" onClick={() => navigate("/estoque/reposicao")}>
                    Repor
                  </Button>
                </div>
              );
            })}
            {data.length > MAX_ITENS && (
              <Button variant="ghost" className="w-full text-xs h-8 text-muted-foreground" onClick={() => navigate("/estoque/reposicao")}>
                Ver todos ({data.length})
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
